/**
 * /flows/:flowId/edit — full-screen builder.
 *
 * Bypasses the parent _app's centred main wrapper by absolutely
 * positioning a viewport-sized container under the global header.
 */
import { useCallback, useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { FlowGraph } from "@app/flow-schemas";
import { api } from "../../../../lib/api.js";
import { useGraphStore } from "../../../../flow-builder/state/graph-store.js";
import { useAutosave } from "../../../../flow-builder/state/use-autosave.js";
import { FlowCanvas } from "../../../../flow-builder/canvas/FlowCanvas.js";
import { Inspector } from "../../../../flow-builder/inspector/Inspector.js";
import { ProblemsPanel } from "../../../../flow-builder/validation/ProblemsPanel.js";
import { VersionBar } from "../../../../flow-builder/versioning/VersionBar.js";

export const Route = createFileRoute("/_app/flows/$flowId/edit")({
  component: FlowBuilderPage,
});

interface FlowResponse {
  flow: {
    id: string;
    name: string;
    status: string;
    draftGraph: FlowGraph | null;
    draftUpdatedAt: string | null;
    publishedVersionId: string | null;
  };
  publishedVersion: { id: string; graph: FlowGraph } | null;
  inFlightRunCount: number;
}

function FlowBuilderPage() {
  const { flowId } = Route.useParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const hydrate = useGraphStore((s) => s.hydrate);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get<FlowResponse>(`/flows/${flowId}`);
      const draft =
        r.data.flow.draftGraph ??
        r.data.publishedVersion?.graph ?? {
          entryNodeId: "n1",
          nodes: { n1: { id: "n1", kind: "entry" }, n2: { id: "n2", kind: "end" } },
        };
      hydrate({
        flowId: r.data.flow.id,
        flowName: r.data.flow.name,
        graph: draft,
        serverDraftUpdatedAt: r.data.flow.draftUpdatedAt,
      });
    } catch {
      setError("Failed to load flow");
    } finally {
      setLoading(false);
    }
  }, [flowId, hydrate]);

  useEffect(() => {
    void load();
  }, [load]);

  // Autosave subscribes to the store and PUTs after debounce.
  useAutosave();

  const handleBack = useCallback(() => {
    void navigate({ to: "/flows" });
  }, [navigate]);

  if (loading) {
    return <p style={{ padding: 32 }}>Loading flow…</p>;
  }
  if (error) {
    return (
      <div style={{ padding: 32 }}>
        <p style={{ color: "var(--error)" }}>{error}</p>
        <button onClick={handleBack} className="brut-button">
          Back to flows
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        // Break out of the centered main wrapper so the canvas fills
        // the rest of the viewport. The 100vh - header is approximate;
        // the global header is ~70px.
        position: "fixed",
        top: 70,
        left: 0,
        right: 0,
        bottom: 0,
        background: "var(--paper)",
        display: "flex",
        flexDirection: "column",
        zIndex: 10,
      }}
    >
      <VersionBar
        onReload={() => void load()}
        onPublished={() => void load()}
        onBack={handleBack}
      />
      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        <div style={{ flex: 1, position: "relative" }}>
          <FlowCanvas />
        </div>
        <Inspector />
      </div>
      <ProblemsPanel />
    </div>
  );
}
